import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function formatSelectionLabel(selectedOptions, placeholder) {
  if (selectedOptions.length === 0) {
    return placeholder;
  }
  if (selectedOptions.length === 1) {
    return selectedOptions[0].label;
  }
  return `${selectedOptions.length} sélectionnés`;
}

const SearchableMultiSelect = ({
  id,
  label,
  value = [],
  onChange,
  options = [],
  placeholder = '— Sélectionner —',
  emptyLabel = 'Aucun résultat',
  disabled = false,
  searchable = false,
  multiple = true,
  className = '',
  buttonClassName = 'form-select text-start',
  hideCounter = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const listId = useId();

  const normalizedOptions = useMemo(
    () => options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt)),
    [options],
  );

  const selectedValueSet = useMemo(
    () => new Set((Array.isArray(value) ? value : []).map((item) => String(item))),
    [value],
  );

  const filtered = useMemo(() => {
    if (!searchable) {
      return normalizedOptions;
    }
    const q = search.trim().toLowerCase();
    if (!q) {
      return normalizedOptions;
    }
    return normalizedOptions.filter((opt) => String(opt.label ?? '').toLowerCase().includes(q));
  }, [normalizedOptions, search, searchable]);

  const selectedOptions = useMemo(
    () => normalizedOptions.filter((opt) => selectedValueSet.has(String(opt.value))),
    [normalizedOptions, selectedValueSet],
  );

  const displayLabel = formatSelectionLabel(selectedOptions, placeholder);

  const updateMenuPosition = () => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const viewportPadding = 8;
    const preferredHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxMenuHeight = Math.min(preferredHeight, Math.max(openUpward ? spaceAbove : spaceBelow, 140));
    const width = Math.max(rect.width, 180);

    let left = rect.left;
    if (left + width > window.innerWidth - viewportPadding) {
      left = Math.max(viewportPadding, window.innerWidth - width - viewportPadding);
    }

    setMenuStyle({
      position: 'fixed',
      left,
      width,
      zIndex: 1080,
      maxHeight: maxMenuHeight,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' }
        : { top: rect.bottom + 4, bottom: 'auto' }),
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const handleReposition = () => updateMenuPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
      setSearch('');
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const isSelected = (optValue) => selectedValueSet.has(String(optValue));

  const selectValue = (optValue) => {
    if (!multiple) {
      // Mono : remplace directement sans devoir passer par la croix.
      if (!isSelected(optValue)) {
        onChange([optValue]);
      }
      setOpen(false);
      setSearch('');
      return;
    }

    const next = isSelected(optValue)
      ? value.filter((item) => String(item) !== String(optValue))
      : [...value, optValue];
    onChange(next);
  };

  const toggleOpen = () => {
    if (disabled) {
      return;
    }
    setOpen((prev) => !prev);
    if (open) {
      setSearch('');
    }
  };

  const clearSelection = (event) => {
    event.stopPropagation();
    onChange([]);
  };

  const menu = open && menuStyle
    ? createPortal(
      (
        <div
          ref={menuRef}
          className="searchable-select__menu border rounded shadow-sm bg-base d-flex flex-column"
          style={menuStyle}
        >
          {searchable && (
            <div className="p-2 border-bottom flex-shrink-0">
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Rechercher…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <ul
            id={listId}
            role="listbox"
            aria-multiselectable={multiple}
            className="list-unstyled mb-0 overflow-auto flex-grow-1"
            style={{ maxHeight: '100%' }}
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-secondary-light small">{emptyLabel}</li>
            ) : (
              filtered.map((opt) => {
                const checked = isSelected(opt.value);
                return (
                  <li key={String(opt.value)}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={checked}
                      className={`w-100 text-start border-0 px-3 py-2 d-flex align-items-center gap-2 ${
                        checked ? 'bg-light' : 'bg-transparent'
                      }`}
                      onClick={() => selectValue(opt.value)}
                    >
                      {multiple ? (
                        <input
                          type="checkbox"
                          className="form-check-input m-0 flex-shrink-0"
                          checked={checked}
                          readOnly
                          tabIndex={-1}
                        />
                      ) : null}
                      <span className={checked ? 'fw-medium' : ''}>{opt.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ),
      document.body,
    )
    : null;

  return (
    <div className={className}>
      {label && <span className="form-label d-block">{label}</span>}
      <div ref={rootRef} className="searchable-select position-relative">
        <button
          ref={buttonRef}
          type="button"
          id={id}
          className={`${buttonClassName} ${value.length === 0 ? 'text-secondary-light' : ''}`.trim()}
          onClick={toggleOpen}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={listId}
        >
          {displayLabel}
        </button>

        {value.length > 0 && (
          <button
            type="button"
            className="searchable-select__clear btn btn-sm position-absolute top-50 translate-middle-y p-0 border-0 bg-transparent text-secondary-light"
            onClick={clearSelection}
            aria-label="Effacer la sélection"
            title="Effacer"
          >
            ×
          </button>
        )}

        {menu}
      </div>
      {!hideCounter && multiple && value.length > 0 && (
        <small className="text-secondary-light mt-1 d-block">
          {value.length} sélectionné{value.length > 1 ? 's' : ''}
        </small>
      )}
    </div>
  );
};

export default SearchableMultiSelect;
